import React, { useEffect, useMemo, useState } from "react";
import { fetchProfile, fetchRoles } from "../api/authApi";
import { apiUrl } from "../taskpane";

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = `${base64}${"=".repeat((4 - (base64.length % 4)) % 4)}`;
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2);
};

const STATUS_COLOR = {
  active: "#22c55e", draft: "#f59e0b", completed: "#3b82f6",
  archived: "#6b7280", pending: "#a78bfa", cancelled: "#ef4444",
};
const STATUS_LABEL = {
  active: "აქტიური", draft: "მუშავდება", completed: "დასრულებული",
  archived: "არქივი", pending: "მოლოდინში", cancelled: "გაუქმებული",
};
const ROLE_LABEL = { admin: "ადმინი", redactor: "რედაქტორი", viewer: "მნახველი" };
const ROLE_COLOR = { admin: "#06b6d4", redactor: "#22c55e", viewer: "#94a3b8" };

function fmtDate(value) {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString("ka-GE");
}

function sColor(s) { return STATUS_COLOR[(s || "").toLowerCase()] || "#94a3b8"; }
function sLabel(s) { return STATUS_LABEL[(s || "").toLowerCase()] || s || "—"; }

export default function ProfilePage({ token, isAdmin, isSuperuser, projects = [], onBack, onOpenProject, onSignOut }) {
  const [apiProfile, setApiProfile] = useState(null);
  const [roleByProject, setRoleByProject] = useState({});
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setApiProfile(null); return; }
    fetchProfile(token)
      .then((data) => { if (!cancelled) setApiProfile(data || null); })
      .catch(() => { if (!cancelled) setApiProfile(null); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    if (!token) { setRoleByProject({}); return; }
    fetchRoles(token)
      .then((roles) => {
        if (cancelled) return;
        const map = {};
        (roles || []).forEach((r) => {
          if (r?.project_id != null) map[String(r.project_id)] = String(r.role || "").toLowerCase();
        });
        setRoleByProject(map);
      })
      .catch(() => { if (!cancelled) setRoleByProject({}); });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    if (!token) return undefined;
    fetch(apiUrl("activity-logs?limit=200"), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) => { if (!cancelled) setActivity(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (!cancelled) setActivity([]); });
    return () => { cancelled = true; };
  }, [token]);

  const profile = useMemo(() => {
    const payload = parseJwtPayload(token) || {};
    const resolvedEmail = apiProfile?.email || payload.email || payload.preferred_username || "-";
    const fallbackName = resolvedEmail.includes("@") ? resolvedEmail.split("@")[0] : resolvedEmail;
    return {
      id: apiProfile?.id ? String(apiProfile.id) : String(payload.sub || "-"),
      name: apiProfile?.name || payload.name || fallbackName || "User",
      email: resolvedEmail,
    };
  }, [token, apiProfile]);

  const roleLabel = isSuperuser ? "სუპერ ადმინი" : isAdmin ? "ადმინი" : "მომხმარებელი";
  const roleColor = isSuperuser ? "#a78bfa" : isAdmin ? "#06b6d4" : "#94a3b8";

  const uid = Number(profile.id);
  const list = Array.isArray(projects) ? projects : [];
  const myProjects = list.filter((p) => !Number.isNaN(uid) && Number(p?.admin) === uid);
  // Projects someone else created where this user has an explicit role.
  const memberProjects = list.filter(
    (p) => Number(p?.admin) !== uid && roleByProject[String(p.id)]
  );
  // Superusers also see every other project without being a member of it.
  const otherProjects = list.filter(
    (p) => Number(p?.admin) !== uid && !roleByProject[String(p.id)]
  );

  return (
    <div style={{ minHeight: "100vh", background: "var(--page-gradient)", padding: "20px 16px", color: "var(--text-primary)" }}>
      <button className="link" onClick={onBack}>← უკან</button>

      {/* ── Identity card ── */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 20, marginBottom: 20 }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%",
          background: "linear-gradient(135deg, #06b6d4, #7c3aed)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0,
        }}>
          {getInitials(profile.name)}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {profile.name}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis" }}>
            {profile.email}
          </div>
          <span style={{
            display: "inline-block", marginTop: 5, fontSize: 10, padding: "2px 10px",
            borderRadius: 10, background: `${roleColor}20`, color: roleColor,
            border: `1px solid ${roleColor}50`, fontWeight: 600,
          }}>
            {roleLabel}
          </span>
          <span style={{ marginLeft: 8, fontSize: 10, color: "var(--text-muted)" }}>ID {profile.id}</span>
        </div>
      </div>

      {/* ── Stats ── */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 20 }}>
        <StatBox label="სულ პროექტი" value={list.length} color="#3b82f6" />
        <StatBox label="შექმნილი" value={myProjects.length} color="#06b6d4" />
        <StatBox label="წევრი ვარ" value={memberProjects.length} color="#a78bfa" />
      </div>

      {/* ── My recent activity ── */}
      {(() => {
        const mine = activity.filter((l) => Number(l.user_id) === Number(profile.id) && l.action !== "LOGIN").slice(0, 6);
        if (!mine.length) return null;
        return (
          <>
            <SectionLabel>ბოლო აქტივობა</SectionLabel>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 16 }}>
              {mine.map((l) => (
                <div key={l.id} style={{ display: "flex", gap: 8, fontSize: 11, padding: "6px 10px", borderRadius: 8, background: "var(--surface-soft)", border: "1px solid var(--surface-border)" }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {l.detail || l.action}
                  </span>
                  <span style={{ color: "var(--text-muted)", flexShrink: 0 }}>
                    {l.created_at ? new Date(l.created_at).toLocaleString("ka-GE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                  </span>
                </div>
              ))}
            </div>
          </>
        );
      })()}

      {/* ── My created projects ── */}
      {myProjects.length > 0 && (
        <>
          <SectionLabel>ჩემი პროექტები</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {myProjects.map((p) => (
              <ProjectRow key={p.id} project={p} role={roleByProject[String(p.id)]} onClick={() => onOpenProject?.(p.id)} />
            ))}
          </div>
        </>
      )}

      {/* ── Projects where member ── */}
      {memberProjects.length > 0 && (
        <>
          <SectionLabel>წევრი ვარ</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {memberProjects.map((p) => (
              <ProjectRow key={p.id} project={p} role={roleByProject[String(p.id)]} onClick={() => onOpenProject?.(p.id)} />
            ))}
          </div>
        </>
      )}

      {otherProjects.length > 0 && (
        <>
          <SectionLabel>{isSuperuser ? "სხვა პროექტები" : "პროექტები"}</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
            {otherProjects.map((p) => (
              <ProjectRow key={p.id} project={p} role={roleByProject[String(p.id)]} onClick={() => onOpenProject?.(p.id)} />
            ))}
          </div>
        </>
      )}

      {typeof onSignOut === "function" && (
        <button
          onClick={onSignOut}
          style={{
            width: "100%", marginTop: 8, padding: "9px 12px", borderRadius: 8, cursor: "pointer",
            background: "transparent", color: "#ef4444", border: "1px solid #ef444460", fontWeight: 600,
          }}
        >
          გასვლა
        </button>
      )}

      {projects.length === 0 && (
        <div style={{ color: "var(--text-muted)", fontSize: 13, textAlign: "center", paddingTop: 24 }}>
          პროექტები ვერ მოიძებნა
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div style={{
      background: "var(--surface-soft)", border: `1px solid ${color}30`,
      borderRadius: 10, padding: "14px", textAlign: "center",
    }}>
      <div style={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 5 }}>{label}</div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 700, color: "var(--text-muted)",
      letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 8,
    }}>
      {children}
    </div>
  );
}

function ProjectRow({ project, role, onClick }) {
  const color = sColor(project.status);
  const roleColor = ROLE_COLOR[role] || "#94a3b8";
  const meta = [project.location, project.area ? `${project.area} მ²` : "", fmtDate(project.date)]
    .filter(Boolean)
    .join(" · ");
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", padding: "9px 12px",
        background: "var(--surface-soft)", border: "1px solid rgba(148,163,184,0.15)",
        borderRadius: 8, cursor: "pointer", color: "var(--text-primary)",
        display: "flex", alignItems: "center", gap: 10,
      }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = "rgba(148,163,184,0.4)")}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(148,163,184,0.15)")}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {project.name}
        </div>
        {meta && (
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{meta}</div>
        )}
      </div>
      {role && (
        <span style={{
          fontSize: 10, padding: "2px 7px", borderRadius: 10, flexShrink: 0,
          background: `${roleColor}20`, color: roleColor, border: `1px solid ${roleColor}50`,
        }}>
          {ROLE_LABEL[role] || role}
        </span>
      )}
      {project.status && (
        <span style={{
          fontSize: 10, padding: "2px 7px", borderRadius: 10, flexShrink: 0,
          background: `${color}20`, color, border: `1px solid ${color}50`,
        }}>
          {sLabel(project.status)}
        </span>
      )}
    </button>
  );
}
